import React, { isValidElement } from "react";
import { AvatarProps } from "./types";
import { AvatarSlot } from "./components/AvatarSlot";
import { sizeStyles } from "shared/src/styles/sizeStyles";
import cx from "components/src/utils/cx";
import {
  tailwindSemanticBorderColors,
  tailwindSemanticBorderSizes,
} from "shared/src/styles/borderStyles";
import { randomIdString } from "shared/src/utils/randomIdString";
import { SemanticColorType } from "shared/src/styles/colorStyles";
import { resolveAvatarImage } from "./js/resolveAvatarImage";
import { cssColor } from "shared/src/utils/cssUtils";
import { addProps } from "shared/src/utils/addProps";
export const Avatar = ({
  size = "2xl",
  className = "",
  of = "athlete",
  bgColor = "neutral",
  bgElement,
  type = "circle",
  src = "",
  icon = "",
  iconColor = "",
  id,
  border = 1,
  showBorder = true,
  borderColor = "fg-subtle" as SemanticColorType,
  altText = "",
  mode = "light",
  style,
  leadingSlot,
  trailingSlot,
  wrapperClasses = "",
  onClick = () => {},
  ...rest
}: AvatarProps) => {
  const { sizeStyle, sizeClasses, sizeMap } = sizeStyles(size);

  const _id = id ? `${id}` : `cui__avatar-${randomIdString()}`;
  const _image = resolveAvatarImage({
    icon,
    iconColor,
    altText,
    mode,
    src,
    of,
    sizeClasses,
    size,
    showBorder
  });
  const _bgColor = /^cui-|tw-/.test(bgColor) ? bgColor : cssColor(bgColor);

  let _borderColor = cssColor(borderColor);
  const hasSemanticColorBorder = borderColor in tailwindSemanticBorderColors;
  if (hasSemanticColorBorder)
    _borderColor = tailwindSemanticBorderColors[borderColor];
  const hasBgElement = isValidElement(bgElement);

  return (
    <div className={`cui__avatar--wrapper cui-relative cui-inline-flex ${!!size ? sizeClasses : ""} ${wrapperClasses}`}>
      <div
        className={cx(
          {
            [sizeClasses as string]: !!size,
            "cui-rounded-[9999px]": type === "circle",
            "cui-bg-bg-layer-two": bgColor === "neutral",
            [tailwindSemanticBorderSizes[border]]: border !== "none",
            "cui-border-solid": border !== "none",
            [tailwindSemanticBorderColors[borderColor]]:
              hasSemanticColorBorder && (border !== "none" || !showBorder),
            "cui-border-neutrals-transparent": !showBorder,
            [className]: true,
          },
          "cui__avatar cui-relative cui-box-border cui-overflow-hidden cui-grid cui-place-items-center cui__color_icon"
        )}
        id={_id}
        onClick={(e) => onClick(e, _id)}
        style={{
          background: hasBgElement ? "var(--neutrals-transparent)" : _bgColor,
          borderColor:
            !hasSemanticColorBorder && !!_borderColor ? _borderColor : "",
          ...style,
        }}
        {...rest}
      >
        {_image}
        {hasBgElement
          ? addProps(bgElement, {
              wrapperClasses: "cui-absolute cui-inset-0 cui-z-[0]",
            })
          : null}
      </div>
      {leadingSlot && isValidElement(leadingSlot.slot) ? (
        <AvatarSlot
          align="left"
          sizeMap={sizeMap}
          {...leadingSlot}
        >
          {leadingSlot.slot}
        </AvatarSlot>
      ) : null}
      {trailingSlot && isValidElement(trailingSlot.slot) ? (
        <AvatarSlot
          align="right"
          sizeMap={sizeMap}
          {...trailingSlot}
        >
          {trailingSlot.slot}
        </AvatarSlot>
      ) : null}
    </div>
  );
};